/* eslint-disable no-magic-numbers -- surface tokens are named by number */

import styled, { css } from 'styled-components';
import { IconName } from '../icon/IconName';
import { LinkButton } from '../button';
import { transition } from '../themes/mainTheme';

type CardBaseProps = {
	$tall?: boolean;
	$disabled?: boolean;
	$icon?: IconName;
};

const cardBaseStyles = css<CardBaseProps & { $useBackgroundColor?: boolean }>`
  border-radius: 4px;
  background-color: ${props => props.theme.card.background};
  display: flex;
  min-width: 300px;
  background: ${({ $useBackgroundColor, theme }) => $useBackgroundColor
		? `linear-gradient(360deg, ${theme.surface[3]} -0.17%, ${theme.surface[4]} 100%)`
		: theme.card.background};
  flex-direction: ${({ $tall }) => $tall ? 'column' : 'row'};
  overflow: hidden;
  font-family: ${props => props.theme.font};
  transition: ${transition('background')};
  padding: 16px;

  ${({ $disabled }) => $disabled && css`
    pointer-events: none;
    opacity: 0.5;
  `};
`;

export const ContentCardRoot = styled.div<CardBaseProps & { $clickable?: boolean, $useBackgroundColor?: boolean }>`
  ${cardBaseStyles};

  ${({ $clickable, $icon }) => $clickable ? css`
    cursor: pointer;

    &:hover {
      background: ${({ theme }) => theme.surface[4]};

      ${ContentCardAction} {
        transform: ${$icon === 'arrow-right' ? 'translateX(2px)' : 'none'};
      }
    }

    &:active,
    &:focus {
      border: 0px solid transparent;
    }
  ` : css`
    cursor: default;
  `};
`;

export const ContentCardImageWrapper = styled.div<{
	$tall?: boolean;
}>`
  border-radius: 4px;
  overflow: hidden;

  ${({ $tall }) => $tall ? css`
    width: 100%;
    max-height: 160px;
  ` : css`
    width: 40px;
    height: 40px;
    flex-shrink: 0;
  `};
`;

export const ContentCardImageIcon = styled.div<{
	$tall?: boolean;
}>`
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.card.subtitle};

  ${({ $tall }) => $tall ? css`
    width: 100%;
    flex: 1;
    min-height: 0;
    font-size: 48px;
  ` : css`
    width: 40px;
    height: 40px;
    flex-shrink: 0;
    font-size: 24px;
  `};
  // The card is a LinkButton, whose icon rule would size this to the button's glyph.
  .icon {
    font-size: inherit;
  }
`;

export const ContentCardBody = styled.div<{
	$tall?: boolean;
}>`
  display: flex;
  flex-direction: row;
  width: 100%;
  align-items: ${({ $tall }) => $tall ? 'flex-end' : 'center'};
  gap: 8px;
  padding-top: ${({ $tall }) => $tall ? '16px' : '0'};
  padding-left: ${({ $tall }) => $tall ? '0' : '8px'};
`;

export const ContentCardText = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  text-align: left;
`;

export const ContentCardTitleRow = styled.span<{
    $tall?: boolean;
  }>`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

export const ContentCardTitle = styled.h5<{ $tall?: boolean }>`
  // Colour lives here, not on a \`p\` descendant of the row — the title is an h5,
  // so the old selector never matched it and the heading fell back to inherit.
  color: ${props => props.theme.card.title};
  font-size: ${({ $tall }) => $tall ? '16px' : '14px'};
  line-height: 140%;
  margin: 0;
  font-weight: ${({ $tall }) => $tall ? '600' : '400'};
  `;

export const ContentCardSubtitle = styled.p<{
    $tall?: boolean;
  }>`
  color: ${props => props.theme.card.subtitle};
  font-size: ${({ $tall }) => $tall ? '14px' : '12px'};
  font-weight: 400;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  margin: 0;
  `;

export const ContentCardDescription = styled.p`
  color: ${props => props.theme.card.color};
  font-size: 14px;
  font-weight: 400;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  margin: 0;
`;

export const ContentCardAction = styled.div<{ $icon?: IconName }>`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  color: ${props => props.theme.card.subtitle};
  transition: ${ ({ $icon }) => $icon === 'arrow-right' as IconName ? 'transform 0.2s ease-in-out' : 'none'};
`;

export const ContentCardIcon = styled.div`
  display: flex;
  align-items: center;
  font-size: 32px;
  color: ${props => props.theme.card.subtitle};

  .icon {
    font-size: inherit;
  }
`;

export const ContentCardButtonRow = styled.div`
  display: flex;
  align-items: flex-start;
`;

const LOGO_SIZE = 24;

export const ContentCardLogo = styled.img`
  width: ${LOGO_SIZE}px;
  height: ${LOGO_SIZE}px;
  object-fit: contain;
  flex-shrink: 0;
  margin-right: 8px;
`;

export const ContentCardLink = styled(LinkButton)<CardBaseProps & { $useBackgroundColor?: boolean }>`
  ${cardBaseStyles};
  cursor: pointer;
  text-decoration: none;

  /* Reset LinkButton styles that leak through */
  justify-content: flex-start;
  align-items: stretch;
  border: none;
  padding: 16px;
  gap: 0;
  min-width: 300px;
  // Button sizes are a fixed height now; a card is as tall as its content.
  height: auto;
  min-height: auto;
  line-height: normal;
  color: inherit;
  background: ${({ $useBackgroundColor, theme }) => $useBackgroundColor
		? `linear-gradient(360deg, ${theme.surface[3]} -0.17%, ${theme.surface[4]} 100%)`
		: theme.card.background};


  & ${ContentCardAction} .icon {
    color: inherit;
  }

  &:hover,
  &.hover {
    border: none;
    color: inherit;
    // Hover is a single surface step up — no gradient, no elevation shadow.
    background: ${({ theme }) => theme.surface[4]};

    & ${ContentCardAction} .icon {
      color: inherit;
    }

    ${ContentCardAction} {
      transform: ${({ $icon }) => $icon === 'arrow-right' as IconName ? 'translateX(2px)' : 'none'};
    }
  }
`;
